'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard,
  AlertTriangle,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Settings,
  HelpCircle,
  LogOut,
  FileText,
  Beaker,
  ClipboardList,
} from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  href: string;
  badge?: number;
  group: string;
}

const navItems: NavItem[] = [
  {
    id: 'nav-overview',
    label: 'EHS Overview',
    icon: <LayoutDashboard size={18} />,
    href: '/ehs-overview-dashboard',
    group: 'Core',
  },
  {
    id: 'nav-incidents',
    label: 'Incidents & Near-Miss',
    icon: <AlertTriangle size={18} />,
    href: '/incident-near-miss-analytics',
    badge: 7,
    group: 'Core',
  },
  {
    id: 'nav-risk',
    label: 'Risk & Compliance',
    icon: <ShieldCheck size={18} />,
    href: '/risk-compliance-management',
    badge: 3,
    group: 'Core',
  },
  {
    id: 'nav-moc',
    label: 'MOC Pipeline',
    icon: <ClipboardList size={18} />,
    href: '/risk-compliance-management',
    group: 'Management',
  },
  {
    id: 'nav-sds',
    label: 'Safety Data Sheets',
    icon: <FileText size={18} />,
    href: '/risk-compliance-management',
    group: 'Management',
  },
  {
    id: 'nav-hygiene',
    label: 'Industrial Hygiene',
    icon: <Beaker size={18} />,
    href: '/risk-compliance-management',
    group: 'Management',
  },
];

const groups = ['Core', 'Management'];

interface SidebarProps {
  currentPath: string;
}

export default function Sidebar({ currentPath }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className={`
        flex flex-col h-full bg-[hsl(var(--sidebar-bg))] border-r border-[hsl(var(--sidebar-border))]
        transition-all duration-300 ease-in-out shrink-0
        ${collapsed ? 'w-16' : 'w-60'}
      `}
    >
      {/* Logo */}
      <div className={`flex items-center h-16 px-4 border-b border-[hsl(var(--sidebar-border))] ${collapsed ? 'justify-center' : 'gap-3'}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-semibold text-sm tracking-tight text-[hsl(var(--foreground))] truncate">
            EHSDashboard
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-4 px-2">
        {groups.map((group) => {
          const items = navItems.filter((n) => n.group === group);
          return (
            <div key={`group-${group}`} className="mb-4">
              {!collapsed && (
                <p className="text-[10px] font-600 uppercase tracking-widest text-[hsl(var(--muted-foreground))] px-3 mb-1">
                  {group}
                </p>
              )}
              {items.map((item) => {
                const isActive = currentPath === item.href;
                return (
                  <Link
                    key={item.id}
                    href={item.href}
                    title={collapsed ? item.label : undefined}
                    className={`
                      group flex items-center gap-3 px-3 py-2 rounded-lg mb-0.5 text-sm font-medium
                      transition-all duration-150
                      ${isActive
                        ? 'bg-[hsl(var(--primary)/0.12)] text-[hsl(var(--primary))]'
                        : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))]'
                      }
                      ${collapsed ? 'justify-center' : ''}
                    `}
                  >
                    <span className={`shrink-0 ${isActive ? 'text-[hsl(var(--primary))]' : ''}`}>
                      {item.icon}
                    </span>
                    {!collapsed && (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.badge !== undefined && (
                          <span className="ml-auto text-[10px] font-600 bg-[hsl(var(--negative)/0.15)] text-[hsl(var(--negative))] rounded-full px-1.5 py-0.5 tabular-nums">
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="border-t border-[hsl(var(--sidebar-border))] py-3 px-2 space-y-0.5">
        {[
          { id: 'nav-settings', icon: <Settings size={16} />, label: 'Settings' },
          { id: 'nav-help', icon: <HelpCircle size={16} />, label: 'Help & Docs' },
          { id: 'nav-logout', icon: <LogOut size={16} />, label: 'Sign Out' },
        ].map((item) => (
          <button
            key={item.id}
            title={collapsed ? item.label : undefined}
            className={`
              w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium
              text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))]
              transition-all duration-150
              ${collapsed ? 'justify-center' : ''}
            `}
          >
            {item.icon}
            {!collapsed && <span>{item.label}</span>}
          </button>
        ))}

        {/* User */}
        {!collapsed && (
          <div className="flex items-center gap-2.5 px-3 py-2 mt-1 rounded-lg bg-[hsl(var(--muted)/0.5)]">
            <div className="w-7 h-7 rounded-full bg-[hsl(var(--primary)/0.2)] flex items-center justify-center text-[hsl(var(--primary))] text-xs font-700">
              MR
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-600 truncate text-[hsl(var(--foreground))]">Marcus Reyes</p>
              <p className="text-[10px] text-[hsl(var(--muted-foreground))] truncate">EHS Director</p>
            </div>
          </div>
        )}
      </div>

      {/* Collapse Toggle */}
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="absolute bottom-32 -right-3 w-6 h-6 rounded-full bg-[hsl(var(--card))] border border-[hsl(var(--border))] flex items-center justify-center text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] shadow-sm transition-all duration-150 z-10"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}